/**
 * Which agents this account does callbacks for.
 *
 * Arun: *"and i can add multiple agents to one account right? … ideally, i would like a tab under
 * post call hook that has a button - Add new agent for callback - once i click, it should ask me to
 * select an agent and show me list of agents for this accoiunt that can have call back. when i
 * clikc on an agent, you will sho me the setup required for each agent under this so its easy to
 * keep trac. right how all agents are dumped here even ones that i dont want and its hard to keep
 * track and work on the agents we need."*
 *
 * Several agents per account always worked. What was missing was anywhere to **say which ones
 * matter**: the hook, routing and context screens each opened with a dropdown of every live agent
 * on the tenant — 129 of them on arun — so the working set existed only in the FDE's head and
 * every screen asked them to find it again.
 *
 * So this tab is the list, and each row is a link to that agent's own page where its whole setup
 * lives. Three things it is careful about:
 *
 *  - **Adding an agent changes nothing on the tenant.** It is a note to ourselves about which
 *    agents we are working on. The dialog and the toast both say so, because "Add" on a screen
 *    full of live tenant data reads like it might install something.
 *  - **It is not a filter.** A callback from an agent nobody added is still accepted and still
 *    dialled — dropping it would lose a real customer's callback over a bookkeeping row. Those
 *    agents get a callout at the top instead, because an agent sending callbacks that nobody is
 *    tracking is worth one click to fix.
 *  - **Every status here is local.** The hook state is whatever the last live read wrote onto the
 *    row, and "never checked" is shown as itself rather than as "not installed". That is what
 *    makes this list render on the day a tenant's API key dies, which is the day someone comes
 *    looking at it.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ChevronsRight,
  MinusCircle,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { SettingsSection } from "@/components/ui/settings-section";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DenseTableViewport } from "@/components/ui/dense-table-viewport";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/ui/empty-state";
import { TableSkeleton } from "@/components/table-skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  errorMessage,
  type AccountConfig,
  type AgentCandidate,
  type CallbackAgent,
  type CallbackAgentsResponse,
} from "@/lib/api";
import { relativeTime } from "@/lib/labels";
import { TABLE_ROW_GROUP_CLASS } from "@/lib/table-styles";
import { cn } from "@/lib/utils";
import { useAddCallbackAgent, useCallbackAgents, useRefreshChannels } from "@/lib/queries";
import { RoutesSection } from "./routes-section";

export function AgentsSection({
  accountId,
  config,
}: {
  accountId: string;
  config: AccountConfig;
}): React.ReactElement {
  const query = useCallbackAgents(accountId);
  const refresh = useRefreshChannels(accountId);
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const columns = useAgentColumns(accountId);

  const data = query.data;
  const agents = data?.agents ?? [];
  const neverRefreshed = Boolean(data) && data!.channels_refreshed_at === null;

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Which agents do callbacks?"
        // The reader's question, then the two facts that stop this being read as an install
        // or as a filter.
        description="One row per agent you are setting up. Adding an agent here changes nothing in 8loop — it is how you say which agents to work on, and each row opens that agent's own setup. Callbacks from an agent that is not listed are still accepted and still dialled."
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {query.isPending
                ? "Loading…"
                : agents.length === 0
                  ? "No agents on the list yet."
                  : `${agents.length} agent${agents.length === 1 ? "" : "s"} on the list.`}
            </p>
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus className="size-3.5" />
              Add an agent
            </Button>
          </div>
        }
      >
        {query.isPending && <TableSkeleton columnCount={5} rowCount={3} />}

        {query.isError && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Couldn't read this account's agent list: {errorMessage(query.error)}
            </AlertDescription>
          </Alert>
        )}

        {/*
          The candidates come out of the cached channel list, so with an empty cache the Add
          dialog has nothing to offer and "no agents found" would read as a fact about the
          tenant. Say whose fault it is, and put the fix here rather than folded away under
          the channel list at the bottom of this page.
        */}
        {neverRefreshed && (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>
                This tenant's agents and channels have never been read from 8loop, so there is
                nothing to choose from yet.
                {data?.channels_refresh_error
                  ? ` The last attempt failed: ${data.channels_refresh_error}`
                  : ""}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={refresh.isPending}
                onClick={() =>
                  refresh.mutate(undefined, {
                    onSuccess: (r) => toast.success(r.message),
                    onError: (e) => toast.error(errorMessage(e)),
                  })
                }
              >
                <RefreshCw className={refresh.isPending ? "size-3.5 animate-spin" : "size-3.5"} />
                {refresh.isPending ? "Reading 8loop…" : "Read them now"}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {data && data.unlisted.length > 0 && (
          <UnlistedCallout accountId={accountId} unlisted={data.unlisted} />
        )}

        {data && agents.length === 0 && !neverRefreshed && (
          <EmptyState
            size="compact"
            title="No agents set up for callbacks yet"
            description="Press Add an agent, pick the one you want to work on, and its page will walk you through the one thing it needs: the post-call hook that turns its finished calls into callbacks here."
          />
        )}

        {/*
          A table, not a stack of cards. Arun: *"possible to show the which agents need callbacl
          as a table that goes in instead of a card - for an account we can have maybe 7-8 agnets
          that need this"*. Seven rows of five columns is scannable down each column, which is the
          question this list is for — which of these is not ready? Cards make that a read of every
          card in turn. Not paged: this list is bounded by how many agents somebody set up by
          hand, and it does not grow with time the way the queue does.
        */}
        {agents.length > 0 && (
          <DenseTableViewport>
            <DataTable
              columns={columns}
              data={agents}
              onRowClick={(row) =>
                navigate(`/accounts/${accountId}/agents/${encodeURIComponent(row.agent_id)}`)
              }
              // The row navigates, so it has to say so: `TableRow` only brings a hover
              // background, and a <tr> without this computes `cursor: auto` — an I-beam over
              // text, which is the exact defect Arun reported on the accounts table.
              rowClassName={`${TABLE_ROW_GROUP_CLASS} cursor-pointer`}
              rowHeightClassName="h-14"
              headerHeightClassName="h-8"
              stickyHeader
            />
          </DenseTableViewport>
        )}
      </SettingsSection>

      {/*
        The account-wide context list is gone from the UI, and this is the third time Arun asked
        for it:

          *"wont contenxt sent be apart of agnets too? since these will be fields defined at agent
          level? … cntext needed changes agent to agent roght?"*
          *"why is this not per agent? what if i want tis for soem agents and not others? whaats
          the justification here?"*
          *"why is this not per agent and at accoint level? this is confusing no? this means every
          agent needs to have that feld craeted and is confusing. felds are diofferent for doffent
          agents. i alraedy gave feedback on this."*

        There was a justification and it was thin: a name every agent on the account happens to
        declare identically could be set once. On a tenant with more than one agent that is rare,
        on equentis it is impossible, and the app's own screen had been printing "No agent on this
        account can be sent callback_context at call start" for days — an account-level setting
        that could not work on a single agent.

        What replaced it is better on every axis: `callback_context` and `previous_<field>` are
        sent to any agent that declares the name, per agent, decided in that agent's own hook
        setup. So there is nothing left for this card to configure.

        Existing rows are untouched and still honoured — `first_name` on equentis is real work,
        four of five agents accept it, and the dialer drops it for the fifth. They now show on
        each agent's page, under what its callbacks carry, which is where they can be judged.
      */}

      {/*
        Every channel on the tenant, folded. Each agent's own channels are on its page, which is
        where the work happens; this stays reachable because "why isn't channel X here?" and
        "which agent owns this number?" are questions only the full list answers — and because it
        carries the Refresh channels button the rest of this page depends on.
      */}
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-1.5 text-sm font-medium text-muted-foreground select-none marker:content-[''] hover:text-foreground">
          <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
          <span>
            <span className="group-open:hidden">Show</span>
            <span className="hidden group-open:inline">Hide</span> every channel on this tenant and
            where it calls back
          </span>
        </summary>
        <div className="pt-4">
          <RoutesSection accountId={accountId} config={config} />
        </div>
      </details>

      {adding && data && (
        <AddAgentDialog accountId={accountId} data={data} onClose={() => setAdding(false)} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------- the columns ----

/**
 * Five columns, in setup order: who it is, does 8loop send us its calls, can we call back, what
 * the callback knows, and has anything actually happened.
 *
 * The two failures that silently strand callbacks — no hook, and no channel that can dial — are
 * the two middle columns, in red where they apply, so the answer to "which of these is not ready?"
 * is a glance down two columns rather than a read of every row.
 */
function useAgentColumns(accountId: string): ColumnDef<CallbackAgent>[] {
  return useMemo(
    () => [
      {
        id: "agent",
        /*
          The five widths add up to 718px on purpose. This card is `max-w-3xl` with `px-6`, and
          `DenseTableViewport` is `overflow-x-hidden` — so a total over that does not scroll, it
          silently amputates the last column. The first cut of this table lost Callbacks
          entirely off the right edge.
        */
        header: "Agent",
        size: 206,
        cell: ({ row }) => {
          const a = row.original;
          return (
            <div className="flex min-w-0 flex-col justify-center gap-0.5 leading-snug">
              {/*
                A real <Link>, not a <span>: the row click stays a convenience, and the name
                carries what people expect of a name — pointer, hover underline, focus ring,
                keyboard reach, middle-click into a new tab.
              */}
              <Link
                to={`/accounts/${accountId}/agents/${encodeURIComponent(a.agent_id)}`}
                onClick={(e) => e.stopPropagation()}
                title={a.agent_name ?? undefined}
                className="truncate rounded-sm font-medium hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                {a.agent_name ?? `Agent ${a.agent_id}`}
              </Link>
              <span className="flex items-center gap-1.5">
                {/* The id, and not the note. Every auto-added row carries the same sentence
                    ("Added automatically: this agent has already sent callbacks"), so a note
                    column here is five copies of one string where the distinguishing fact — the
                    id — should be. The note is read in full on the agent's own page. */}
                <span className="truncate font-mono text-[11px] text-muted-foreground">
                  {a.agent_id}
                </span>
                {a.archived && (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    archived
                  </Badge>
                )}
                {a.missing_from_tenant && (
                  <Badge variant="outline" className="shrink-0 text-[10px] text-warning">
                    not on tenant
                  </Badge>
                )}
              </span>
            </div>
          );
        },
      },
      {
        id: "hook",
        header: "Post-call hook",
        size: 160,
        cell: ({ row }) => {
          const hook = hookState(row.original);
          const title = hook.line;
          return (
            <div className="flex min-w-0 items-start gap-2 leading-snug" title={title}>
              {hook.icon}
              <div className="min-w-0">
                <p className={cn("truncate text-xs", hook.className)}>{hook.short}</p>
                <p className="truncate text-[11px] text-muted-foreground">{hook.detail}</p>
              </div>
            </div>
          );
        },
      },
      {
        id: "dial",
        header: "Can it call back?",
        size: 148,
        cell: ({ row }) => {
          const a = row.original;
          if (a.channels.total === 0) {
            return (
              <span
                className="text-xs text-destructive"
                title="This agent has no channel, so nothing can dial its callbacks."
              >
                No channel
              </span>
            );
          }
          if (a.channels.none_can_dial) {
            return (
              <div
                className="flex min-w-0 flex-col justify-center leading-snug"
                title="None of this agent's channels can place a call, so its callbacks are recorded and never dialled. Fix it with an override on the agent's page."
              >
                <span className="truncate text-xs text-destructive">No channel can dial</span>
                <span className="truncate text-[11px] text-muted-foreground">
                  recorded, never rung
                </span>
              </div>
            );
          }
          return (
            <div className="flex min-w-0 flex-col justify-center leading-snug">
              <span className="truncate text-xs">
                {a.channels.dialable} of {a.channels.total} channel
                {a.channels.total === 1 ? "" : "s"}
              </span>
              <span className="truncate text-[11px] text-muted-foreground">
                {a.channels.overridden > 0
                  ? `${a.channels.overridden} sent elsewhere`
                  : "dials back on itself"}
              </span>
            </div>
          );
        },
      },
      /*
        There is no Context column, and this is why.

        It counted `field_mappings` — the explicit source → target rows an older version of this
        screen created. Context does not travel that way any more: `callback_context` and the
        `previous_<field>` names are sent to any agent that declares them, decided per agent in
        block 2 of its hook setup. So an agent wired correctly by convention read "none" here,
        under a tooltip that said out loud "Nothing is passed into this agent's callbacks — the
        call opens with no context." A confident false negative on the one tab that summarises
        every agent.

        Nothing cheap replaces it: whether an agent will accept `callback_context` is a fact
        about that agent's own settable fields, which this list does not carry. It is answered
        exactly where it is decided — the agent's page, where the pickers, the inherited mappings
        and the dropped-field warning all live together. A column of guesses is worse than a
        column that is not there.
      */
      {
        id: "callbacks",
        header: "Callbacks",
        size: 96,
        cell: ({ row }) => {
          const a = row.original;
          return (
            <span
              className={cn(
                "text-xs tabular-nums",
                a.callbacks_seen === 0 && "text-muted-foreground",
              )}
              /* The count alone in 82px, and the two readings of a zero in the tooltip: "we have
                 never heard from it" and "the hook is live and nobody has asked yet" are
                 different situations, and only the second is worth waiting on. */
              title={
                a.callbacks_seen > 0
                  ? `${a.callbacks_seen} callback${
                      a.callbacks_seen === 1 ? "" : "s"
                    } received from this agent since it was added ${relativeTime(a.added_at)}`
                  : a.hook.present === true
                    ? "None since the hook was installed."
                    : "None — nothing from this agent has ever reached this app."
              }
            >
              {a.callbacks_seen}
            </span>
          );
        },
      },
    ],
    [accountId],
  );
}

/**
 * The hook, as a verdict, a second line, and the whole sentence for the tooltip.
 *
 * `present === null` is its own state: nobody has asked 8loop yet, or the ask failed. Reporting
 * that as "not installed" sends an FDE to reinstall a hook that may already be live — so it gets
 * a grey dash and the word "unknown", never a red cross.
 *
 * `line` is the full sentence, on the cell's `title`, because the column is 200px wide and the
 * 8loop error text that matters most is the longest thing here.
 */
function hookState(agent: CallbackAgent): {
  icon: React.ReactElement;
  /** The verdict, three or four words. */
  short: string;
  /** When it was checked, or why it could not be. */
  detail: string;
  /** The whole thing, for `title`. */
  line: string;
  className?: string;
} {
  const when = agent.hook.checked_at ? relativeTime(agent.hook.checked_at) : null;

  if (agent.hook.present === true) {
    return {
      icon: <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />,
      short: "Installed",
      detail: when ? `${agent.hook.version ?? "?"} · ${when}` : (agent.hook.version ?? "version ?"),
      line: `Post-call hook installed on version ${agent.hook.version ?? "?"}${
        when ? ` — checked ${when}` : ""
      }`,
      className: "text-success",
    };
  }
  // Installed, and running on nothing. A third state since installing stopped publishing: a
  // webhook on a draft exists on the agent and fires on no call at all, so a tick here would
  // say the job is done while every finished call goes nowhere, and a cross would send
  // somebody to install what is already installed.
  if (agent.hook.staged_version) {
    return {
      icon: <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />,
      short: "Not published",
      detail: `on draft ${agent.hook.staged_version}`,
      line:
        `The hook is on this agent's draft ${agent.hook.staged_version} and fires on nothing — ` +
        `8loop runs the webhooks belonging to the version a call ran on. Publish the agent in ` +
        `8loop, or hand over a fresh brief and let your Claude Code do the publish step` +
        `${when ? ` — checked ${when}` : ""}.`,
      className: "text-warning",
    };
  }
  if (agent.hook.present === false) {
    return {
      icon: <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />,
      short: "Not installed",
      detail: when ? `checked ${when}` : "sends nothing here",
      line: `No post-call hook from this app, so this agent sends nothing here${
        when ? ` — checked ${when}` : ""
      }`,
      className: "text-destructive",
    };
  }
  return {
    icon: <MinusCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />,
    short: "Unknown",
    detail: agent.hook.error ? "8loop could not be read" : "never checked",
    line: agent.hook.error
      ? `Hook state unknown — 8loop could not be read${when ? ` ${when}` : ""}: ${agent.hook.error}`
      : "Hook state unknown — this agent has not been checked against 8loop yet. Open it to check.",
    className: "text-muted-foreground",
  };
}

// --------------------------------------------------------------- the unlisted ----

/** Agents sending callbacks that nobody added. One click each, because they are already live. */
function UnlistedCallout({
  accountId,
  unlisted,
}: {
  accountId: string;
  unlisted: CallbackAgentsResponse["unlisted"];
}): React.ReactElement {
  const add = useAddCallbackAgent(accountId);
  return (
    <Alert>
      <AlertTriangle className="size-4" />
      <AlertDescription className="space-y-2">
        <p>
          {unlisted.length === 1
            ? "1 agent is already sending callbacks to this app but is not on the list"
            : `${unlisted.length} agents are already sending callbacks to this app but are not on the list`}
          . Their callbacks are being queued and dialled as normal — adding them just puts their
          setup where you can see it.
        </p>
        <ul className="space-y-1">
          {unlisted.map((u) => (
            <li key={u.agent_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="font-medium">{u.agent_name ?? `Agent ${u.agent_id}`}</span>
              <span className="text-muted-foreground">
                {u.callbacks_seen} callback{u.callbacks_seen === 1 ? "" : "s"}, last{" "}
                {relativeTime(u.last_seen_at)}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs"
                disabled={add.isPending}
                onClick={() =>
                  add.mutate(
                    { agent_id: u.agent_id, agent_name: u.agent_name },
                    {
                      onSuccess: (r) => toast.success(r.message),
                      onError: (e) => toast.error(errorMessage(e)),
                    },
                  )
                }
              >
                <Plus className="size-3" />
                Add
              </Button>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

// ------------------------------------------------------------- the add dialog ----

/**
 * Pick an agent to set up.
 *
 * Everything here is local — the candidates are the cached channel list minus the agents already
 * on the list — so the dialog opens with content rather than a spinner.
 *
 * Blocked and archived agents are **listed with their reason, not hidden**. An agent whose only
 * channel is a campaign dialler cannot dial its own callbacks, but that is fixable with an
 * override and the FDE may well be adding it for exactly that reason. Hiding it would leave them
 * searching for an agent they can see in 8loop.
 */
function AddAgentDialog({
  accountId,
  data,
  onClose,
}: {
  accountId: string;
  data: CallbackAgentsResponse;
  onClose: () => void;
}): React.ReactElement {
  const add = useAddCallbackAgent(accountId);
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.candidates;
    return data.candidates.filter(
      (c) =>
        (c.agent_name ?? "").toLowerCase().includes(needle) ||
        c.agent_id.toLowerCase().includes(needle),
    );
  }, [data.candidates, q]);

  // Straight to the agent's page on success — "when i clikc on an agent, you will sho me the
  // setup required for each agent under this". Landing back on the list would leave the reader
  // to find the row they just created.
  const choose = (c: AgentCandidate) =>
    add.mutate(
      { agent_id: c.agent_id, agent_name: c.agent_name },
      {
        onSuccess: (r) => {
          toast.success(r.message);
          onClose();
          navigate(`/accounts/${accountId}/agents/${encodeURIComponent(c.agent_id)}`);
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>Add an agent for callbacks</DialogTitle>
          <DialogDescription>
            These are the agents on this tenant that are not on the list yet. Picking one adds it
            here and opens its setup — nothing is written to 8loop from this app at all: that page
            asks you two questions and produces a brief you hand to your Claude Code.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="agent-search" className="sr-only">
            Search agents
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
            <Input
              id="agent-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or id"
              className="pl-8"
            />
          </div>
        </div>

        <div className="-mx-1 max-h-[46vh] overflow-y-auto px-1">
          {data.candidates.length === 0 ? (
            <EmptyState
              size="compact"
              title={
                data.channels_refreshed_at === null
                  ? "This tenant's agents have not been read yet"
                  : "Every agent on this tenant is already on the list"
              }
              description={
                data.channels_refreshed_at === null
                  ? "Close this and press Read them now, at the top of the page. Until then there is nothing to choose from — this list comes from the channels we read from 8loop."
                  : "Nothing left to add. An agent created in 8loop since the last channel refresh will not be here yet — press Refresh channels under the channel list."
              }
            />
          ) : matches.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-muted-foreground">
              No agent matches “{q}”.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {matches.map((c) => (
                <li key={c.agent_id}>
                  <button
                    type="button"
                    disabled={add.isPending}
                    onClick={() => choose(c)}
                    className="flex w-full cursor-pointer items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">
                        {c.agent_name ?? `Agent ${c.agent_id}`}
                        <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
                          {c.agent_id}
                        </span>
                      </p>
                      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <span>
                          {c.channels} channel{c.channels === 1 ? "" : "s"}
                          {c.dialable_channels > 0 && `, ${c.dialable_channels} can dial`}
                        </span>
                        {c.callbacks_seen > 0 && (
                          <span>
                            {c.callbacks_seen} callback{c.callbacks_seen === 1 ? "" : "s"} already
                          </span>
                        )}
                        {c.archived && (
                          <Badge variant="outline" className="text-[10px]">
                            archived in 8loop
                          </Badge>
                        )}
                      </p>
                      {/* Not a reason to hide it: an override on this agent's page fixes it, and
                          hiding it would leave someone hunting for an agent they can see in
                          8loop. */}
                      {c.blocked_reason && (
                        <p className="mt-1 flex items-start gap-1.5 text-xs text-warning">
                          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                          <span>{c.blocked_reason}</span>
                        </p>
                      )}
                    </div>
                    <ChevronsRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <p className="mr-auto self-center text-[11px] text-muted-foreground">
            {data.channels_refreshed_at
              ? `Agent list read from 8loop ${relativeTime(data.channels_refreshed_at)}`
              : "Agent list has never been read from 8loop"}
          </p>
          <Button variant="ghost" onClick={onClose} disabled={add.isPending}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
